import { validateName } from "@/lib/validateName";

export interface KnowledgeFolder {
  id: string;
  businessId: string;
  name: string;
  parentId: string | null;
  createdAt: number;
}

export interface KnowledgePageFolder {
  id: string;
  businessId: string;
  page: string;
  raw: boolean;
  folderId: string;
}

export const MAX_KNOWLEDGE_FOLDERS = 200;
export const MAX_KNOWLEDGE_FOLDER_DEPTH = 8;
export const MAX_KNOWLEDGE_FOLDER_NAME_LENGTH = 60;

export class KnowledgeFolderError extends Error {
  constructor(message: string, readonly status = 400) {
    super(message);
    this.name = "KnowledgeFolderError";
  }
}

export function knowledgeFolderName(value: unknown): string {
  try {
    return validateName(value, "Folder");
  } catch (error) {
    throw new KnowledgeFolderError(error instanceof Error ? error.message : "Invalid folder name");
  }
}

/** Validates an entire business's independent hierarchy, including imported trees. */
export function validateKnowledgeFolderHierarchy(folders: KnowledgeFolder[]): void {
  if (folders.length > MAX_KNOWLEDGE_FOLDERS) {
    throw new KnowledgeFolderError(`A knowledge base can have at most ${MAX_KNOWLEDGE_FOLDERS} folders`);
  }
  const byId = new Map(folders.map(folder => [folder.id, folder]));
  if (byId.size !== folders.length) throw new KnowledgeFolderError("Folder IDs must be unique");
  const siblingNames = new Set<string>();
  for (const folder of folders) {
    const name = knowledgeFolderName(folder.name);
    if (name !== folder.name) throw new KnowledgeFolderError("Folder names must not start or end with spaces");
    const siblingKey = JSON.stringify([folder.businessId, folder.parentId, name.toLowerCase()]);
    if (siblingNames.has(siblingKey)) throw new KnowledgeFolderError(`A folder named "${name}" already exists here`, 409);
    siblingNames.add(siblingKey);
    const seen = new Set<string>();
    let current: KnowledgeFolder | undefined = folder;
    while (current) {
      if (seen.has(current.id)) throw new KnowledgeFolderError("Cannot move a folder inside itself");
      seen.add(current.id);
      if (seen.size > MAX_KNOWLEDGE_FOLDER_DEPTH) {
        throw new KnowledgeFolderError(`Folders can be nested at most ${MAX_KNOWLEDGE_FOLDER_DEPTH} levels deep`);
      }
      if (!current.parentId) break;
      const parent = byId.get(current.parentId);
      if (!parent || parent.businessId !== folder.businessId) throw new KnowledgeFolderError("Parent folder not found", 404);
      current = parent;
    }
  }
}
